import React, { useEffect, useState, useMemo, useCallback } from "react";

const CardComponent = ({
  item,
  containerWidth = 400,
  selectedBankIds,
  selectedBranchIds,
  savedQueries,
  getThemeURL,
  getData,
  instanceBankIds,
}) => {
  const [contentItems, setContentItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  console.log("card-savedQueries", savedQueries);
  console.log("card-instanceBankIds", instanceBankIds);

  // Get styles from cardStyles
  const styleMap = item?.cardStyles || {};
  const fieldsConfig = item?.fieldsConfig || [];

  // Memoized style function
  const getStyleForKey = useCallback(
    (key) => {
      const color = styleMap[`${key}_color`] || "#374151";
      const fontSize = styleMap[`${key}_fontSize`] || 12;
      const textAlignment = styleMap[`${key}_textAlignment`] || "left";
      const isIdField = key.startsWith("id_");

      return {
        color,
        fontSize: `${fontSize}px`,
        fontWeight: isIdField ? "bold" : "normal",
        backgroundColor: isIdField ? "#FEE2E2" : "transparent",
        padding: isIdField ? "4px 6px" : "2px 0",
        borderRadius: isIdField ? "4px" : "0",
        textAlign: textAlignment,
      };
    },
    [styleMap]
  );

  const isNumber = useCallback((value) => {
    return !isNaN(value) && !isNaN(parseFloat(value));
  }, []);

  const formatValue = useCallback((key, value) => {
    if (
      key.toLowerCase().includes("percentage") ||
      key.toLowerCase().includes("percent")
    ) {
      return `${value}%`;
    }
    return value;
  }, []);

  // Function to check if a field should be excluded from display
  const shouldExcludeField = useCallback((key) => {
    const excludedFields = [
      "bank_id",
      "bankId",
      "Bank_ID",
      "branch_id",
      "branchId",
      "Branch_ID",
      "branchdetails_id",
    ];
    return excludedFields.includes(key);
  }, []);

  // Function to check if a field is enabled in fieldsConfig
  const isFieldEnabled = useCallback(
    (key) => {
      if (!fieldsConfig || fieldsConfig.length === 0) {
        return true;
      }

      const fieldConfig = fieldsConfig.find(
        (config) => config.fieldName === key
      );

      if (!fieldConfig) {
        return true;
      }

      return fieldConfig.enabled;
    },
    [fieldsConfig]
  );

  const renderPercentage = useCallback(
    (key, value) => {
      const percentage = parseFloat(value);
      if (isNaN(percentage)) return value;

      const color =
        percentage >= 80 ? "#10b981" : percentage >= 60 ? "#f59e0b" : "#ef4444";

      const arrowDirection =
        percentage >= 80
          ? "M12 4l8 8h-6v8h-4v-8H4l8-8z"
          : percentage >= 60
          ? "M12 6l-8 8h5v8h6v-8h5l-8-8z"
          : "M12 21l-8-8h5V5h6v8h5l-8 8z";

      const textAlignment = styleMap[`${key}_textAlignment`] || "right";
      const justifyContent =
        textAlignment === "center"
          ? "center"
          : textAlignment === "left"
          ? "flex-start"
          : "flex-end";

      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            justifyContent: justifyContent,
            flexWrap: "wrap",
          }}
        >
          <svg
            width={Math.max(10, containerWidth * 0.02)}
            height={Math.max(10, containerWidth * 0.02)}
            viewBox="0 0 24 24"
            fill={color}
          >
            <path d={arrowDirection} />
          </svg>
          <span>{percentage}%</span>
        </div>
      );
    },
    [styleMap, containerWidth]
  );

  // Transform NiFi data to card format - Complete function with aggregatedData support
  const transformNiFiDataToCardFormat = useCallback((nifiResponse) => {
    console.log("Card: Raw API Response received:", nifiResponse);

    try {
      // PRIORITY 1: Handle aggregated data structure (NEW)
      if (nifiResponse && nifiResponse.aggregatedData) {
        console.log(
          "Card: Processing aggregatedData:",
          nifiResponse.aggregatedData
        );

        const { aggregatedData } = nifiResponse;

        // Check if aggregated data is successful and has data
        if (
          aggregatedData.status === "success" &&
          aggregatedData.data &&
          Array.isArray(aggregatedData.data)
        ) {
          console.log("Card: Aggregated data validation passed");

          // If data is already in object format (like your JSON)
          if (
            aggregatedData.data.length > 0 &&
            typeof aggregatedData.data[0] === "object"
          ) {
            console.log(
              "Card: Aggregated data is in object format, returning:",
              aggregatedData.data
            );
            return aggregatedData.data;
          }

          // If data is array of arrays with columns (alternative format)
          if (
            aggregatedData.columns &&
            Array.isArray(aggregatedData.columns) &&
            aggregatedData.data.length > 0 &&
            Array.isArray(aggregatedData.data[0])
          ) {
            console.log("Card: Transforming aggregated data from array format");

            const transformedData = aggregatedData.data.map((row) => {
              const dataPoint = {};

              aggregatedData.columns.forEach((column, index) => {
                // Clean column names by removing SQL functions and table prefixes
                let cleanKey = column
                  .replace(/^(SUM|COUNT|AVG|MIN|MAX|DISTINCT)\(/i, "")
                  .replace(/\)$/, "")
                  .replace(/^.*\./, ""); // Remove table prefix like "table.column"

                // Handle potential date fields (timestamps)
                let value = row[index];

                // Check if this is a timestamp field and convert it
                if (
                  cleanKey.toLowerCase().includes("date") &&
                  typeof value === "number" &&
                  value > 1000000000000
                ) {
                  try {
                    value = new Date(value).toISOString().split("T")[0];
                  } catch (error) {
                    console.warn(
                      "Card: Error converting timestamp:",
                      value,
                      error
                    );
                  }
                }

                dataPoint[cleanKey] = value;
              });

              return dataPoint;
            });

            console.log(
              "Card: Transformed aggregated data from array format:",
              transformedData
            );
            return transformedData;
          }
        } else if (aggregatedData.recordCount === 0) {
          console.log("Card: Aggregated data has no records matching criteria");
          return [];
        } else {
          console.log("Card: Aggregated data validation failed:", {
            status: aggregatedData.status,
            hasData: !!aggregatedData.data,
            isArray: Array.isArray(aggregatedData.data),
            dataLength: aggregatedData.data ? aggregatedData.data.length : 0,
          });
        }
      }

      // PRIORITY 2: Handle multi-bank response structure (existing logic - fallback)
      else if (
        nifiResponse &&
        nifiResponse.responses &&
        Array.isArray(nifiResponse.responses)
      ) {
        console.log("Card: Processing multi-bank responses (fallback)");
        let allTransformedData = [];

        // Process each bank's response
        nifiResponse.responses.forEach((bankResponse, bankIndex) => {
          console.log(
            `Card: Processing bank ${bankResponse.bankId} response:`,
            bankResponse
          );

          if (bankResponse.status === "success" && bankResponse.data) {
            const bankData = bankResponse.data;

            // Check if this bank has data
            if (
              bankData.status === "success" &&
              bankData.data &&
              Array.isArray(bankData.data)
            ) {
              // If data is already in object format
              if (
                bankData.data.length > 0 &&
                typeof bankData.data[0] === "object"
              ) {
                console.log(
                  `Card: Bank ${bankResponse.bankId} data is already in object format`
                );
                allTransformedData = allTransformedData.concat(bankData.data);
              }
              // If data is array of arrays with columns
              else if (
                bankData.columns &&
                Array.isArray(bankData.columns) &&
                bankData.data.length > 0 &&
                Array.isArray(bankData.data[0])
              ) {
                console.log(
                  `Card: Transforming Bank ${bankResponse.bankId} data from array format`
                );

                const transformedBankData = bankData.data.map((row) => {
                  const dataPoint = {};

                  bankData.columns.forEach((column, index) => {
                    // Clean column names by removing SQL functions and table prefixes
                    let cleanKey = column
                      .replace(/^(SUM|COUNT|AVG|MIN|MAX|DISTINCT)\(/i, "")
                      .replace(/\)$/, "")
                      .replace(/^.*\./, ""); // Remove table prefix

                    // Handle potential date fields (timestamps)
                    let value = row[index];

                    // Check if this is a timestamp field and convert it
                    if (
                      cleanKey.toLowerCase().includes("date") &&
                      typeof value === "number" &&
                      value > 1000000000000
                    ) {
                      try {
                        value = new Date(value).toISOString().split("T")[0];
                      } catch (error) {
                        console.warn(
                          "Card: Error converting timestamp:",
                          value,
                          error
                        );
                      }
                    }

                    dataPoint[cleanKey] = value;
                  });

                  // Add bank identification if not present
                  if (!dataPoint.bank_id && !dataPoint.bankId) {
                    dataPoint.bank_id = bankResponse.bankId;
                  }

                  // Add bank name from the data if available and not present
                  if (!dataPoint.bank_name && !dataPoint.bankName) {
                    const bankNameIndex = bankData.columns.findIndex(
                      (col) =>
                        col.toLowerCase().includes("bank_name") ||
                        col.toLowerCase().includes("bankname")
                    );
                    if (bankNameIndex !== -1 && row[bankNameIndex]) {
                      dataPoint.bank_name = row[bankNameIndex];
                    }
                  }

                  return dataPoint;
                });

                allTransformedData =
                  allTransformedData.concat(transformedBankData);
              }
            } else if (bankData.recordCount === 0) {
              console.log(
                `Card: Bank ${bankResponse.bankId} has no records matching criteria`
              );
            }
          } else {
            console.warn(
              `Card: Bank ${bankResponse.bankId} response failed:`,
              bankResponse
            );
          }
        });

        console.log(
          "Card: Final transformed multi-bank data:",
          allTransformedData
        );
        return allTransformedData;
      }

      // PRIORITY 3: Handle single response format (fallback for backward compatibility)
      else if (
        nifiResponse &&
        nifiResponse.status === "success" &&
        nifiResponse.data
      ) {
        console.log("Card: Processing single response format (fallback)");
        const { data } = nifiResponse;

        // If data is already in object format, use it directly
        if (
          Array.isArray(data) &&
          data.length > 0 &&
          typeof data[0] === "object"
        ) {
          console.log(
            "Card: Single response - data is already in object format"
          );
          return data;
        }

        // If data is array of arrays with columns
        if (
          nifiResponse.columns &&
          Array.isArray(data) &&
          Array.isArray(data[0])
        ) {
          const { columns } = nifiResponse;
          const transformedData = data.map((row) => {
            const dataPoint = {};
            columns.forEach((column, index) => {
              let cleanKey = column
                .replace(/^(SUM|COUNT|AVG|MIN|MAX|DISTINCT)\(/i, "")
                .replace(/\)$/, "")
                .replace(/^.*\./, "");

              let value = row[index];
              if (
                cleanKey.toLowerCase().includes("date") &&
                typeof value === "number" &&
                value > 1000000000000
              ) {
                try {
                  value = new Date(value).toISOString().split("T")[0];
                } catch (error) {
                  console.warn(
                    "Card: Error converting timestamp:",
                    value,
                    error
                  );
                }
              }

              dataPoint[cleanKey] = value;
            });
            return dataPoint;
          });
          console.log(
            "Card: Single response - transformed data from array format"
          );
          return transformedData;
        }
      }

      console.log(
        "Card: No valid data structure found in response, returning empty array"
      );
      return [];
    } catch (error) {
      console.error("Card: Error transforming NiFi data:", error);
      return [];
    }
  }, []);

  // Helper function to check if query is bank-only (core.bank without joins)
  const isBankOnlyQuery = useCallback((queryObject) => {
    const entity = queryObject.entity || "";
    const joins = queryObject.joins || [];

    // Check if entity is core.bank and no joins are present
    const isCoreBank =
      entity.toLowerCase().includes("core.bank") ||
      entity.toLowerCase() === "bank";
    const hasNoJoins = joins.length === 0;

    console.log("Card: Checking if bank-only query:", {
      entity,
      isCoreBank,
      hasNoJoins,
      joins,
    });

    return isCoreBank && hasNoJoins;
  }, []);

  // Memoized function to fetch filtered data from NiFi - WITH BANK-ONLY CHECKS
  const fetchFilteredData = useCallback(
    async (bankIds, branchIds) => {
      try {
        // Check if required functions are available
        if (!getData || typeof getData !== "function") {
          console.log(
            "Card: getData function not available, skipping NiFi filtering"
          );
          return Array.isArray(item?.data) ? item.data : [];
        }

        if (!getThemeURL) {
          console.log(
            "Card: getThemeURL not available, skipping NiFi filtering"
          );
          return Array.isArray(item?.data) ? item.data : [];
        }

        if (!item?.dataSource || !savedQueries || savedQueries.length === 0) {
          console.log("No data source or saved queries available for card");
          return Array.isArray(item?.data) ? item.data : [];
        }

        const currentDashboardWidgetId = `WIDGET_${item.id}`;
        const matchedQuery = savedQueries.find(
          (q) => q.dashboardWidgetId === currentDashboardWidgetId
        );

        console.log("Card matchedQuery", matchedQuery);
        console.log("Card serviceName", item.dataSource);

        if (!matchedQuery) {
          console.log(
            "No matching query found for card data source:",
            item.dataSource
          );
          return Array.isArray(item?.data) ? item.data : [];
        }

        // Get query object
        const queryObject =
          typeof matchedQuery.queryBuilder === "string"
            ? JSON.parse(matchedQuery.queryBuilder)
            : matchedQuery.queryBuilder;

        // Check if this is a bank-only query
        const isBankOnly = isBankOnlyQuery(queryObject);
        console.log("Card: Is bank-only query:", isBankOnly);

        if (isBankOnly) {
          // BANK-ONLY SCENARIOS
          const hasBankIds = bankIds && bankIds.length > 0;
          const hasBranchIds = branchIds && branchIds.length > 0;

          console.log("Card: Bank-only query scenarios:", {
            hasBankIds,
            hasBranchIds,
            bankIds,
            branchIds,
          });

          // Scenario 2: Only branch IDs are available (no bank IDs)
          if (!hasBankIds && hasBranchIds) {
            console.log(
              "Card: Scenario 2 - Only branch IDs available for bank-only query, returning empty data"
            );
            return [];
          }

          // Scenario 1: Only bank IDs are available
          // Scenario 3: Both bank and branch IDs are available (use only bank IDs)
          if (hasBankIds) {
            console.log(
              `Card: Scenario ${
                hasBranchIds ? "3" : "1"
              } - Using bank IDs for bank-only query`
            );

            const filters = {
              "bank.id": {
                operator: "IN",
                value: bankIds,
              },
            };

            const enhancedQuery = {
              ...queryObject,
              filters: {
                ...(queryObject.filters || {}),
                ...filters,
              },
            };

            console.log(
              "Card: Enhanced bank-only query:",
              JSON.stringify(enhancedQuery, null, 2)
            );

            // Same auth logic as before
            let userResponse;
            try {
              userResponse = await getData(getThemeURL);
            } catch (error) {
              console.error("Card: Error fetching user profile:", error);
              return Array.isArray(item?.data) ? item.data : [];
            }

            if (
              !userResponse ||
              !Array.isArray(userResponse) ||
              userResponse.length === 0
            ) {
              console.error(
                "Card: Failed to fetch user profile or invalid response"
              );
              return Array.isArray(item?.data) ? item.data : [];
            }

            const user = userResponse[0];
            const roleName = user.role_name;
            const userBankId = user.bank_id;

            const authObject = {
              instanceUrl: "/query-builder-api/execute",
              apiType: "POST",
            };

            const processBankIds = (ids) => {
              if (!ids) return [];

              if (Array.isArray(ids)) {
                return ids.map((id) => Number(id));
              } else if (typeof ids === "string") {
                return ids.split(",").map((id) => Number(id.trim()));
              } else {
                return [Number(ids)];
              }
            };

            // Set auth based on user role
            if (roleName === "SUPER ADMIN") {
              authObject.type = "SA";
              authObject.bank_id = processBankIds(instanceBankIds);
            } else if (user.is_admin === true) {
              authObject.type = "HO";
              authObject.bank_id = processBankIds(userBankId);
            } else {
              authObject.type = "U";
              authObject.bank_id = processBankIds(userBankId);
            }

            const payloadWithAuth = {
              auth: authObject,
              main: enhancedQuery,
            };

            setLoading(true);
            setError(null);

            const response = await fetch("/nifi/fetchQuery-API", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(payloadWithAuth),
            });

            console.log(
              "Card: Bank-only query payload:",
              JSON.stringify(payloadWithAuth)
            );

            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            console.log("Card: Bank-only query result:", result);

            if (result.status === "error") {
              throw new Error(result.message || "Query execution failed");
            }

            // Transform the result
            const transformedData = transformNiFiDataToCardFormat(result);
            console.log("Card: Transformed bank-only data:", transformedData);

            if (
              transformedData &&
              Array.isArray(transformedData) &&
              transformedData.length > 0
            ) {
              const availableFields = Object.keys(transformedData[0] || {});
              const hiddenIdFields = [
                "bank_id",
                "branchdetails_id",
                "branch_id",
              ];

              const configurableFields = availableFields.filter(
                (field) => !hiddenIdFields.includes(field.toLowerCase())
              );

              const enabledFields = item.fieldsConfig
                ? item.fieldsConfig
                    .filter((config) => config.enabled)
                    .map((config) => config.fieldName)
                : configurableFields;

              const idFields = availableFields.filter((field) =>
                hiddenIdFields.includes(field.toLowerCase())
              );
              const allIncludedFields = [...enabledFields, ...idFields];

              const filteredData = transformedData.map((dataItem) => {
                const filteredItem = {};
                allIncludedFields.forEach((field) => {
                  if (dataItem.hasOwnProperty(field)) {
                    filteredItem[field] = dataItem[field];
                  }
                });
                return filteredItem;
              });

              return filteredData;
            }

            return [];
          }

          // No bank IDs and no branch IDs - return empty
          console.log(
            "Card: No bank IDs available for bank-only query, returning empty data"
          );
          return [];
        }

        // NON-BANK-ONLY QUERIES
        else {
          console.log("Card: Processing non-bank-only query");

          // Extract table name from entity
          const entity = queryObject.entity || "";
          const tableName = entity.includes(".")
            ? entity.split(".")[1]
            : entity;

          // Get joins information
          const joins = queryObject.joins || [];
          const joinedTables = joins.map((join) => {
            const table = join.table || join.alias || "";
            return table.includes(".") ? table.split(".")[1] : table;
          });

          // Helper function to determine filter field names
          const determineFilterFields = () => {
            let bankFilterField = null;
            let branchFilterField = null;

            console.log("Card: Table analysis:", { tableName, joinedTables });

            // FIRST CHECK: For any table that is NOT "bank" or "branch_details", use tableName.bank_id and tableName.branch_id
            if (tableName !== "bank" && tableName !== "branch_details") {
              bankFilterField = `${tableName}.bank_id`;
              branchFilterField = `${tableName}.branch_id`;
              return { bankFilterField, branchFilterField };
            }

            // If the first condition doesn't match, proceed with existing join logic
            const hasBankTable =
              tableName === "bank" || joinedTables.includes("bank");
            const hasBranchDetailsTable =
              tableName === "branch_details" ||
              joinedTables.includes("branch_details");
            const hasJoins = joins.length > 0;

            // Determine bank filter field
            if (tableName === "bank") {
              bankFilterField = "bank.id";
              if (!hasJoins) {
                branchFilterField = null;
                return { bankFilterField, branchFilterField };
              }
            } else if (hasBankTable && hasJoins) {
              bankFilterField = "bank.id";
            } else if (
              !hasBankTable &&
              tableName !== "bank" &&
              tableName !== "branch_details"
            ) {
              bankFilterField = `${tableName}.bank_id`;
            } else if (!hasBankTable) {
              bankFilterField = `${tableName}.bank_id`;
            }

            // Determine branch filter field
            if (tableName === "branch_details") {
              branchFilterField = "branch_details.id";
            } else if (hasBranchDetailsTable && hasJoins) {
              branchFilterField = "branch_details.id";
            } else if (
              !hasBranchDetailsTable &&
              tableName !== "bank" &&
              tableName !== "branch_details"
            ) {
              branchFilterField = `${tableName}.branch_id`;
            } else {
              const tableWithBranchId = joinedTables.find(
                (table) => table !== "bank" && table !== "branch_details"
              );
              if (tableWithBranchId) {
                branchFilterField = `${tableWithBranchId}.branch_id`;
              } else {
                branchFilterField = `${tableName}.branch_id`;
              }
            }

            return { bankFilterField, branchFilterField };
          };

          const { bankFilterField, branchFilterField } =
            determineFilterFields();

          console.log("Card: Determined filter fields:", {
            bankFilterField,
            branchFilterField,
          });

          const filters = {};

          // Add bank filter if bankIds provided and field determined
          if (bankIds && bankIds.length > 0 && bankFilterField) {
            filters[bankFilterField] = {
              operator: "IN",
              value: bankIds,
            };
          }

          // Add branch filter if branchIds provided and field determined
          if (branchIds && branchIds.length > 0 && branchFilterField) {
            filters[branchFilterField] = {
              operator: "IN",
              value: branchIds,
            };
          } else if (!branchIds || branchIds.length === 0) {
            if (branchFilterField) {
              filters[branchFilterField] = null;
            }
          }

          const enhancedQuery = {
            ...queryObject,
            filters: {
              ...(queryObject.filters || {}),
              ...filters,
            },
          };

          console.log(
            "Card: Enhanced non-bank-only query:",
            JSON.stringify(enhancedQuery, null, 2)
          );

          // Same auth logic as before
          let userResponse;
          try {
            userResponse = await getData(getThemeURL);
          } catch (error) {
            console.error("Card: Error fetching user profile:", error);
            return Array.isArray(item?.data) ? item.data : [];
          }

          if (
            !userResponse ||
            !Array.isArray(userResponse) ||
            userResponse.length === 0
          ) {
            console.error(
              "Card: Failed to fetch user profile or invalid response"
            );
            return Array.isArray(item?.data) ? item.data : [];
          }

          const user = userResponse[0];
          const roleName = user.role_name;
          const userBankId = user.bank_id;

          const authObject = {
            instanceUrl: "/query-builder-api/execute",
            apiType: "POST",
          };

          const processBankIds = (ids) => {
            if (!ids) return [];

            if (Array.isArray(ids)) {
              return ids.map((id) => Number(id));
            } else if (typeof ids === "string") {
              return ids.split(",").map((id) => Number(id.trim()));
            } else {
              return [Number(ids)];
            }
          };

          // Set auth based on user role
          if (roleName === "SUPER ADMIN") {
            authObject.type = "SA";
            authObject.bank_id = processBankIds(instanceBankIds);
          } else if (user.is_admin === true) {
            authObject.type = "HO";
            authObject.bank_id = processBankIds(userBankId);
          } else {
            authObject.type = "U";
            authObject.bank_id = processBankIds(userBankId);
          }

          const payloadWithAuth = {
            auth: authObject,
            main: enhancedQuery,
          };

          setLoading(true);
          setError(null);

          const response = await fetch("/nifi/fetchQuery-API", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payloadWithAuth),
          });

          console.log(
            "Card: Non-bank-only query payload:",
            JSON.stringify(payloadWithAuth)
          );

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const result = await response.json();
          console.log("Card: Non-bank-only query result:", result);

          if (result.status === "error") {
            throw new Error(result.message || "Query execution failed");
          }

          // Transform and process the result
          const transformedData = transformNiFiDataToCardFormat(result);
          console.log("Card: Final transformed data:", transformedData);

          if (
            transformedData &&
            Array.isArray(transformedData) &&
            transformedData.length > 0
          ) {
            const availableFields = Object.keys(transformedData[0] || {});
            const hiddenIdFields = ["bank_id", "branchdetails_id", "branch_id"];

            const configurableFields = availableFields.filter(
              (field) => !hiddenIdFields.includes(field.toLowerCase())
            );

            const enabledFields = item.fieldsConfig
              ? item.fieldsConfig
                  .filter((config) => config.enabled)
                  .map((config) => config.fieldName)
              : configurableFields;

            const idFields = availableFields.filter((field) =>
              hiddenIdFields.includes(field.toLowerCase())
            );
            const allIncludedFields = [...enabledFields, ...idFields];

            const filteredData = transformedData.map((dataItem) => {
              const filteredItem = {};
              allIncludedFields.forEach((field) => {
                if (dataItem.hasOwnProperty(field)) {
                  filteredItem[field] = dataItem[field];
                }
              });
              return filteredItem;
            });

            return filteredData;
          }

          return [];
        }
      } catch (error) {
        console.error("Card: Error fetching filtered data:", error);
        setError(`Failed to fetch filtered data: ${error.message}`);
        return Array.isArray(item?.data) ? item.data : [];
      } finally {
        setLoading(false);
      }
    },
    [
      item?.dataSource,
      item?.data,
      item?.fieldsConfig,
      item?.id,
      savedQueries,
      transformNiFiDataToCardFormat,
      getData,
      getThemeURL,
      instanceBankIds,
      isBankOnlyQuery,
    ]
  );

  // Memoized safe content items with enhanced validation
  const safeContentItems = useMemo(() => {
    if (!Array.isArray(contentItems)) {
      return [];
    }

    return contentItems.filter((entry) => {
      // Multiple layers of validation
      if (
        !entry ||
        typeof entry !== "object" ||
        entry instanceof Promise ||
        React.isValidElement(entry)
      ) {
        return false;
      }

      // Check for Promise values within the object
      try {
        const hasPromiseValues = Object.values(entry).some(
          (value) => value instanceof Promise
        );
        return !hasPromiseValues;
      } catch (error) {
        console.warn("Error validating entry:", error);
        return false;
      }
    });
  }, [contentItems]);

  // Enhanced data loading with proper Promise handling
  useEffect(() => {
    const loadData = async () => {
      console.log("Card LoadData called with:", {
        selectedBankIds,
        selectedBranchIds,
        hasDataSource: !!item?.dataSource,
        currentDataLength: contentItems.length,
        savedQueriesLength: savedQueries?.length || 0,
        hasGetData: !!getData,
        hasGetThemeURL: !!getThemeURL,
      });

      // Early validation
      if (!item || !item.data || !Array.isArray(item.data)) {
        setContentItems([]);
        return;
      }

      try {
        let resultData;

        // First try to fetch from NiFi if dataSource is available and functions exist
        if (
          item?.dataSource &&
          savedQueries &&
          savedQueries.length > 0 &&
          getData &&
          getThemeURL
        ) {
          console.log("Card: Attempting NiFi filtering:", {
            dataSource: item.dataSource,
            selectedBankIds,
            selectedBranchIds,
          });

          resultData = await fetchFilteredData(
            selectedBankIds,
            selectedBranchIds
          );
        } else {
          // No NiFi data source or missing functions, use local filtering only
          console.log(
            "Card: No NiFi dataSource or missing functions, using local filtering only"
          );

          let filteredData = item.data;
          const hasBankId = filteredData.some(
            (entry) =>
              entry.hasOwnProperty("bank_id") ||
              entry.hasOwnProperty("bankId") ||
              entry.hasOwnProperty("Bank_ID")
          );

          const hasBranchId = filteredData.some(
            (entry) =>
              entry.hasOwnProperty("branchdetails_id") ||
              entry.hasOwnProperty("branch_id") ||
              entry.hasOwnProperty("branchId") ||
              entry.hasOwnProperty("Branch_ID")
          );

          // Priority 1: Filter by branch IDs if any branches are selected
          if (
            selectedBranchIds &&
            selectedBranchIds.length > 0 &&
            hasBranchId
          ) {
            filteredData = filteredData.filter((entry) => {
              const entryBranchId =
                entry.branchdetails_id ||
                entry.branch_id ||
                entry.branchId ||
                entry.Branch_ID;
              return selectedBranchIds.includes(entryBranchId);
            });
          }
          // Priority 2: Filter by bank IDs if no branches selected but banks are selected
          else if (selectedBankIds && selectedBankIds.length > 0 && hasBankId) {
            filteredData = filteredData.filter((entry) => {
              const entryBankId =
                entry.bank_id || entry.bankId || entry.Bank_ID;
              const entryBranchId =
                entry.branchdetails_id ||
                entry.branch_id ||
                entry.branchId ||
                entry.Branch_ID;

              // Check if this record has a branch_id
              const recordHasBranchId =
                entryBranchId !== undefined &&
                entryBranchId !== null &&
                entryBranchId !== "";

              // If record has branch_id, exclude it from bank-level filtering
              if (recordHasBranchId) {
                return false;
              }

              // Only include records that match selected bank and don't have branch_id
              return selectedBankIds.includes(entryBankId);
            });
          }

          resultData = filteredData;
        }

        // Enhanced validation to ensure no Promises in data
        const validatedData = Array.isArray(resultData)
          ? resultData.filter((entry) => {
              // Filter out invalid entries including Promises
              if (
                !entry ||
                typeof entry !== "object" ||
                entry instanceof Promise ||
                React.isValidElement(entry)
              ) {
                return false;
              }

              // Check for Promise values within the object
              const hasPromiseValues = Object.values(entry).some(
                (value) => value instanceof Promise
              );

              return !hasPromiseValues;
            })
          : [];

        setContentItems(validatedData);
      } catch (error) {
        console.error("Error in loadData:", error);
        setError("Failed to load data");
        setContentItems([]);
      }
    };

    loadData();
  }, [
    selectedBankIds,
    selectedBranchIds,
    item,
    fetchFilteredData,
    savedQueries,
    getData,
    getThemeURL,
  ]);

  // Initialize contentItems on component mount with validation
  useEffect(() => {
    if (
      item?.data &&
      Array.isArray(item.data) &&
      item.data.length > 0 &&
      !selectedBankIds?.length &&
      !selectedBranchIds?.length
    ) {
      // Validate initial data
      const validatedData = item.data.filter((entry) => {
        if (
          !entry ||
          typeof entry !== "object" ||
          entry instanceof Promise ||
          React.isValidElement(entry)
        ) {
          return false;
        }

        const hasPromiseValues = Object.values(entry).some(
          (value) => value instanceof Promise
        );

        return !hasPromiseValues;
      });

      setContentItems(validatedData);
    }
  }, [item]);

  console.log("item DATAAAAAAAAAAAAAAA", item);

  // NOW SAFE TO DO EARLY RETURNS AFTER ALL HOOKS ARE DEFINED

  // Early return after hooks if no valid item or data - SHOW NO DATA MESSAGE
  // if (!item || !item.data || !Array.isArray(item.data) || item.data.length < 1) {
  //   return (
  //     <div
  //       style={{
  //         display: "flex",
  //         alignItems: "center",
  //         justifyContent: "center",
  //         height: "100%",
  //         color: "#6b7280",
  //         fontSize: "12px",
  //         textAlign: "center",
  //         padding: "20px",
  //         flexDirection: "column",
  //         gap: "12px",
  //       }}
  //     >
  //       <div
  //         style={{
  //           padding: "10px",
  //           backgroundColor: "#fef3c7",
  //           borderRadius: "8px",
  //           border: "1px solid #f59e0b",
  //         }}
  //       >
  //         <div
  //           style={{
  //             color: "#92400e",
  //             fontWeight: "600",
  //           }}
  //         >
  //           No Data Available
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  // Show loading state
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          color: "#6b7280",
          fontSize: "14px",
          textAlign: "center",
          padding: "20px",
        }}
      >
        <div>Loading filtered data...</div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          color: "#ef4444",
          fontSize: "14px",
          textAlign: "center",
          padding: "20px",
        }}
      >
        <div>Error: {error}</div>
      </div>
    );
  }

  // Show no data message if filtered out (AFTER FILTRATION)
  if (
    safeContentItems.length === 0 &&
    (selectedBankIds?.length > 0 || selectedBranchIds?.length > 0)
  ) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          color: "#6b7280",
          fontSize: "12px",
          textAlign: "center",
          padding: "20px",
          flexDirection: "column",
          gap: "12px",
        }}
      >
        <div
          style={{
            padding: "10px",
            backgroundColor: "#fef3c7",
            borderRadius: "8px",
            border: "1px solid #f59e0b",
          }}
        >
          <div
            style={{
              color: "#92400e",
              fontWeight: "600",
            }}
          >
            No Data Available
          </div>
        </div>
      </div>
    );
  }

  // Show no data message if no safe content items but item has data (edge case)
  if (safeContentItems.length === 0 && item?.data?.length > 0) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          color: "#6b7280",
          fontSize: "12px",
          textAlign: "center",
          padding: "20px",
          flexDirection: "column",
          gap: "12px",
        }}
      >
        <div
          style={{
            padding: "10px",
            backgroundColor: "#fef3c7",
            borderRadius: "8px",
            border: "1px solid #f59e0b",
          }}
        >
          <div
            style={{
              color: "#92400e",
              fontWeight: "600",
            }}
          >
            No Data Available
          </div>
        </div>
      </div>
    );
  }

  // Main render with enhanced validation
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        width: "100%",
        maxWidth: containerWidth,
        padding: "0 16px",
        boxSizing: "border-box",
      }}
    >
      {safeContentItems.map((entry, index) => {
        // Final validation before rendering
        if (
          !entry ||
          typeof entry !== "object" ||
          entry instanceof Promise ||
          React.isValidElement(entry)
        ) {
          console.warn(`Skipping invalid entry at index ${index}:`, entry);
          return null;
        }

        // Check for Promise values within the object
        try {
          const hasPromiseValues = Object.values(entry).some(
            (value) => value instanceof Promise
          );

          if (hasPromiseValues) {
            console.warn(
              `Entry contains Promise values at index ${index}:`,
              entry
            );
            return null;
          }
        } catch (error) {
          console.warn(`Error validating entry at index ${index}:`, error);
          return null;
        }

        const allFields = Object.entries(entry)
          .filter(([key]) => !shouldExcludeField(key))
          .filter(([key]) => isFieldEnabled(key))
          .map(([key, value]) => ({
            key,
            value: formatValue(key, value),
            originalValue: value,
          }));

        if (allFields.length === 0) {
          return null;
        }

        const midPoint = Math.ceil(allFields.length / 2);
        const leftColumnFields = allFields.slice(0, midPoint);
        const rightColumnFields = allFields.slice(midPoint);

        return (
          <div
            key={`card-${index}`}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              width: "100%",
              minHeight: "24px",
              gap: `${Math.max(8, containerWidth * 0.02)}px`,
              padding: "4px 0",
            }}
          >
            {/* Left column */}
            <div
              style={{
                flex: "1",
                minWidth: "0",
                display: "flex",
                flexDirection: "column",
                gap: "2px",
              }}
            >
              {leftColumnFields.map(({ key, value, originalValue }) => {
                const fieldStyle = getStyleForKey(key);
                return (
                  <div
                    key={`left-${key}-${index}`}
                    style={{
                      ...fieldStyle,
                      wordWrap: "break-word",
                      overflowWrap: "break-word",
                      whiteSpace: "normal",
                      hyphens: "auto",
                      lineHeight: "1.4",
                    }}
                  >
                    {key.toLowerCase().includes("percentage") ||
                    key.toLowerCase().includes("percent")
                      ? renderPercentage(key, originalValue)
                      : value}
                  </div>
                );
              })}
            </div>

            {/* Right column */}
            <div
              style={{
                flex: "1",
                minWidth: "0",
                display: "flex",
                flexDirection: "column",
                gap: "2px",
              }}
            >
              {rightColumnFields.map(({ key, value, originalValue }) => {
                const fieldStyle = getStyleForKey(key);
                return (
                  <div
                    key={`right-${key}-${index}`}
                    style={{
                      ...fieldStyle,
                      fontWeight: isNumber(originalValue) ? "600" : "normal",
                      wordWrap: "break-word",
                      overflowWrap: "break-word",
                      whiteSpace: "normal",
                      hyphens: "auto",
                      lineHeight: "1.4",
                    }}
                  >
                    {key.toLowerCase().includes("percentage") ||
                    key.toLowerCase().includes("percent")
                      ? renderPercentage(key, originalValue)
                      : value}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CardComponent;
